import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Translate, I18n } from 'react-redux-i18n';
import * as actions_list from 'redux/actions/watchListActions';
import { Settings, Trash, TrendingUp, TrendingDown } from 'theme/icon';
import { FaGripVertical } from 'react-icons/fa';
import 'assets/css/watchlist.css';

const FormDialog = (props) => {
    const [open, setOpen] = useState(false);
    const [orderedLists, setOrderedLists] = useState([]);
    const [saving, setSaving] = useState(false);
    const [draggedIndex, setDraggedIndex] = useState(null);
    const [dragOverIndex, setDragOverIndex] = useState(null);

    useEffect(() => {
        if (open) {
            setOrderedLists([...(props.list || [])]);
            props.loadLists(props.user_id);
        }
    }, [open, props.list, props.user_id]);

    const handleClickOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleBackdropClick = (event) => {
        if (event.target === event.currentTarget) {
            handleClose();
        }
    };

    const persistOrder = async (nextLists) => {
        setSaving(true);
        try {
            await props.reorderList({
                params: {
                    id: props.user_id,
                    order: nextLists.map((item) => item.id),
                },
                token: props.token,
            });
        } finally {
            setSaving(false);
        }
    };

    const moveItem = async (index, direction) => {
        const targetIndex = index + direction;
        if (targetIndex < 0 || targetIndex >= orderedLists.length) {
            return;
        }

        const nextLists = [...orderedLists];
        const [moved] = nextLists.splice(index, 1);
        nextLists.splice(targetIndex, 0, moved);
        setOrderedLists(nextLists);
        await persistOrder(nextLists);
    };

    const reorderByDrag = async (fromIndex, toIndex) => {
        if (
            fromIndex == null
            || toIndex == null
            || fromIndex === toIndex
            || fromIndex < 0
            || toIndex < 0
            || fromIndex >= orderedLists.length
            || toIndex >= orderedLists.length
        ) {
            return;
        }

        const nextLists = [...orderedLists];
        const [moved] = nextLists.splice(fromIndex, 1);
        nextLists.splice(toIndex, 0, moved);
        setOrderedLists(nextLists);
        await persistOrder(nextLists);
    };

    const handleDragStart = (event, index) => {
        if (saving) {
            event.preventDefault();
            return;
        }

        setDraggedIndex(index);
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('text/plain', String(index));
    };

    const handleDragOver = (event, index) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
        if (draggedIndex !== index) {
            setDragOverIndex(index);
        }
    };

    const handleDrop = async (event, index) => {
        event.preventDefault();
        const fromIndex = draggedIndex ?? Number(event.dataTransfer.getData('text/plain'));
        await reorderByDrag(fromIndex, index);
        setDraggedIndex(null);
        setDragOverIndex(null);
    };

    const handleDragEnd = () => {
        setDraggedIndex(null);
        setDragOverIndex(null);
    };

    const onClickRemove = (item) => {
        setOpen(false);
        props.removeItem({
            id: props.user_id,
            list_id: item.id,
        }, props.token);
    };

    return (
        <div className="watchlist-modal-trigger">
            <button
                type="button"
                className="btn btn-outline watchlist-settings-btn"
                onClick={handleClickOpen}
                aria-label="Manage watchlists"
            >
                <Settings size={20} />
            </button>
            {open && (
                <div
                    className="dialog watchlist-delete-dialog"
                    role="presentation"
                    onClick={handleBackdropClick}
                >
                    <div
                        className="dialog-content watchlist-picker-content"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="watchlist-delete-title"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h2 id="watchlist-delete-title" className="dialog-title">
                            <Translate value="watchlist_manage_title" />
                        </h2>
                        <p className="dialog-content-text">
                            <Translate value="watchlist_reorder_hint" />
                        </p>
                        <p className="dialog-content-text watchlist-delete-hint">
                            <Translate value="watchlist_delete_hint" />
                        </p>

                        <div className="dialog-list-wrap">
                            {orderedLists.length === 0 ? (
                                <p className="dialog-list-empty">
                                    <Translate value="no_watchlists" />
                                </p>
                            ) : (
                                <ul className="dialog-list dialog-list--reorder">
                                    {orderedLists.map((item, index) => (
                                        <li
                                            key={item.id}
                                            className={dragOverIndex === index ? 'dialog-list-item--drag-over' : ''}
                                            onDragOver={(event) => handleDragOver(event, index)}
                                            onDrop={(event) => handleDrop(event, index)}
                                        >
                                            <div
                                                className={`dialog-list-reorder-item ${draggedIndex === index ? 'is-dragging' : ''}`}
                                            >
                                                <button
                                                    type="button"
                                                    className="dialog-list-drag-handle"
                                                    draggable={!saving}
                                                    onDragStart={(event) => handleDragStart(event, index)}
                                                    onDragEnd={handleDragEnd}
                                                    aria-label={I18n.t('watchlist_drag_handle')}
                                                    title={I18n.t('watchlist_drag_handle')}
                                                    disabled={saving}
                                                >
                                                    <FaGripVertical size={14} />
                                                </button>
                                                <span className="dialog-list-item-name">{item.name}</span>
                                                <div className="dialog-list-reorder-actions">
                                                    <button
                                                        type="button"
                                                        className="dialog-list-reorder-btn"
                                                        onClick={() => moveItem(index, -1)}
                                                        disabled={saving || index === 0}
                                                        aria-label={I18n.t('watchlist_move_up')}
                                                        title={I18n.t('watchlist_move_up')}
                                                    >
                                                        <TrendingUp size={16} />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="dialog-list-reorder-btn"
                                                        onClick={() => moveItem(index, 1)}
                                                        disabled={saving || index === orderedLists.length - 1}
                                                        aria-label={I18n.t('watchlist_move_down')}
                                                        title={I18n.t('watchlist_move_down')}
                                                    >
                                                        <TrendingDown size={16} />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="dialog-list-reorder-btn dialog-list-reorder-btn--danger"
                                                        onClick={() => onClickRemove(item)}
                                                        disabled={saving}
                                                        aria-label={I18n.t('delete')}
                                                        title={I18n.t('delete')}
                                                    >
                                                        <Trash size={16} />
                                                    </button>
                                                </div>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>

                        <div className="dialog-actions">
                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={handleClose}
                            >
                                <Translate value="close" />
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

function mapStateToProps(reducer) {
    return {
        user_id: reducer.session.auth.id,
        token: reducer.session.auth.token,
        list: reducer.watchList.list,
    };
}

const mapDispatchToProps = (dispatch) => ({
    loadLists: (user_id) =>
        dispatch(actions_list.loadList({ user_id })),
    removeItem: (params, token) =>
        dispatch(actions_list.removeItem({ params, token })),
    reorderList: (data) =>
        dispatch(actions_list.reorderList(data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(FormDialog);
